import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import { Spinner, Overline, PriorityBadge } from "@/components/Brand";
import { toast } from "sonner";
import { Sparkles, Phone, RefreshCw, Repeat, CreditCard } from "lucide-react";

const STATUS = ["new", "contacted", "interested", "negotiating", "closed"];

export default function ExhibitorLeads() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [analyzing, setAnalyzing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/exhibitor/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const analyzeAll = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/exhibitor/leads/analyze-all");
      toast.success(`AI analysed ${data.analyzed} leads`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setAnalyzing(false); }
  };

  const shown = (leads || []).filter((l) => filter === "all" || l.priority === filter);
  const sorted = [...shown].sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <Shell nav={exhibitorNav}>
      <div className="flex items-center justify-between">
        <div>
          <Overline>Lead Management</Overline>
          <h1 className="font-display text-3xl gold-text mt-2">Your Leads</h1>
        </div>
        <button onClick={analyzeAll} disabled={analyzing} className="btn-gold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-60" data-testid="analyze-all-button">
          {analyzing ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />} AI Prioritise
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto mt-5 pb-1" style={{ scrollbarWidth: "none" }}>
        {["all", "hot", "warm", "cold"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`lead-filter-${f}`} className={`capitalize whitespace-nowrap text-xs px-4 py-2 rounded-full border transition ${filter === f ? "btn-gold border-transparent" : "glass text-slate-300 border-amber-500/20"}`}>
            {f}
          </button>
        ))}
      </div>

      {!leads ? <Spinner /> : sorted.length === 0 ? (
        <p className="text-slate-400 text-center py-16">No leads yet. Scan visitor badges or visiting cards to build leads.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {sorted.map((l) => (
            <div key={l.id} onClick={() => navigate(`/exhibitor/leads/${l.id}`)} className="glass rounded-2xl p-4 card-lift cursor-pointer" data-testid={`lead-card-${l.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-slate-100 font-medium truncate">{l.name || "Unnamed Lead"}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                    {l.source === "visiting_card" ? <CreditCard size={12} /> : <Phone size={12} />}
                    {l.phone || l.company || l.classification}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <PriorityBadge priority={l.priority} />
                  {l.score != null && <span className="text-xs text-slate-400 font-mono-j">{l.score}</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(l.tags || []).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300">#{t}</span>)}
                {l.visit_count > 1 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 flex items-center gap-1"><Repeat size={10} /> {l.visit_count} visits</span>}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] uppercase tracking-wide text-slate-500">{l.followup_status}</span>
                {l.suggested_action && <span className="text-[10px] text-amber-400/80 truncate max-w-[55%]">→ {l.suggested_action}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

export { STATUS };
